import { LoadingButton } from "@mui/lab";
import {
  Divider,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Popper,
  Grid,
  Paper,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { usePopupNotificationContext } from "Context/NotificationPopup";
import { useClients } from "Context/Clients";

const ExtendedControls = ({
  open,
  accountCharts,
  handleOIDClick,
  handleDropdownClick,
  iconButtonRef,
}) => {
  const [openSelect, setOpenSelect] = useState(false);
  const [openClientSelect, setOpenClientSelect] = useState(false);
  const [loadingButtonType, setLoadingButtonType] = useState(null);
  const [selectedEndCustomerArticleId, setSelectedEndCustomerArticleId] =
    useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showSelectClient, setShowSelectClient] = useState(false);
  const { openError, openSuccess } = usePopupNotificationContext();
  const { clients, fetchClients } = useClients();

  const selectRef = useRef(null);
  const menuRef = useRef(null);
  const selectClientRef = useRef(null);
  const menuClientRef = useRef(null);

  // Workaround for Select + ClickAwayListener so ClickAwayListener is not triggered
  useEffect(() => {
    const onMouseUp = (e) => {
      if (e.target.parentElement === selectRef.current) {
        setOpenSelect(true);
      } else {
        if (e.target.parentElement !== menuRef.current) {
          setOpenSelect(false);
        }
      }

      if (e.target.parentElement === selectClientRef.current) {
        setOpenClientSelect(true);
      } else {
        if (e.target.parentElement !== menuClientRef.current) {
          setOpenClientSelect(false);
        }
      }
    };
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const handleAccountNumberClick = async (accountNumber) => {
    setLoadingButtonType(accountNumber);
    const accountChart = accountCharts.find(
      (ac) => ac.AccountNumber === accountNumber
    );
    if (accountChart) {
      await handleDropdownClick(accountChart.Id);
    }
    setLoadingButtonType(null);
  };

  const handleOnOIDClick = async () => {
    setLoadingButtonType("OID");
    await handleOIDClick();
    setLoadingButtonType(null);
  };

  const handleSelectConnect = async () => {
    if (showSelectClient && !selectedClientId) {
      openError("Ingen klient vald");
      return;
    }

    await handleDropdownClick(selectedEndCustomerArticleId, selectedClientId);
  };

  useEffect(() => {
    const selectedChart = accountCharts.find(
      (chart) => chart.Id === selectedEndCustomerArticleId
    );

    setSelectedClientId(null);
    if (selectedChart) {
      setShowSelectClient(selectedChart.AccountNumber === "1340-D");
    }
  }, [selectedEndCustomerArticleId]);

  useEffect(() => {
    fetchClients();
  }, []);

  return (
    <Popper
      open={open}
      anchorEl={iconButtonRef.current}
      placement="bottom-end"
      style={{ zIndex: 1300 }}
    >
      <Grid item container justifyContent="flex-end">
        <Paper
          sx={{ padding: "2rem", maxWidth: 1000, border: "2px solid black" }}
        >
          <Grid container item spacing={1}>
            <Grid item xs={4} md={4}>
              <LoadingButton
                variant="contained"
                fullWidth
                loading={loadingButtonType === "OID"}
                disabled={loadingButtonType !== null}
                onClick={handleOnOIDClick}
              >
                OID
              </LoadingButton>
            </Grid>
            <Grid item xs={4} md={4}>
              <LoadingButton
                variant="contained"
                fullWidth
                loading={loadingButtonType === "3104"}
                disabled={loadingButtonType !== null}
                onClick={() => handleAccountNumberClick("3104")}
              >
                Intäkt påm-3104
              </LoadingButton>
            </Grid>
            <Grid item xs={4} md={4}>
              <LoadingButton
                variant="contained"
                fullWidth
                loading={loadingButtonType === "3120"}
                disabled={loadingButtonType !== null}
                onClick={() => handleAccountNumberClick("3120")}
              >
                Intäkt Ränta-3120
              </LoadingButton>
            </Grid>
            <Grid item xs={12} md={12} margin="5px">
              <Divider />
            </Grid>
            <Grid item xs={8} md={8} display="flex" gap={1}>
              <FormControl fullWidth>
                <InputLabel id="input-label">Välj konto</InputLabel>
                <Select
                  ref={selectRef} // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                  MenuProps={{
                    MenuListProps: {
                      ref: menuRef, // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                    },
                  }}
                  labelId="input-label"
                  label="Välj konto"
                  size="small"
                  open={openSelect}
                  onClose={() => setOpenSelect(false)}
                  value={selectedEndCustomerArticleId}
                  onChange={(e) => {
                    setSelectedEndCustomerArticleId(e.target.value);
                  }}
                >
                  <MenuItem key={null} value={null}>
                    {"Inget"}
                  </MenuItem>
                  {accountCharts.map((account) => (
                    <MenuItem key={account.Id} value={account.Id}>
                      {account.Title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {showSelectClient ? (
                <FormControl fullWidth>
                  {" "}
                  <InputLabel id="input-label">Välj klient</InputLabel>
                  <Select
                    ref={selectClientRef} // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                    MenuProps={{
                      MenuListProps: {
                        ref: menuClientRef, // Workaround for select + ClickAwayListener so ClickAwayListener is not triggered
                      },
                    }}
                    size="small"
                    labelId="input-label"
                    label="Välj klient"
                    open={openClientSelect}
                    onClose={() => setOpenClientSelect(false)}
                    value={selectedClientId}
                    onChange={(e) => {
                      setSelectedClientId(e.target.value);
                    }}
                  >
                    <MenuItem key={null} value={null}>
                      {"Inget"}
                    </MenuItem>
                    {clients.map((client) => (
                      <MenuItem key={client.Id} value={client.Id}>
                        {client.Title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Grid>
            <Grid item xs={4} md={4}>
              <LoadingButton
                variant="contained"
                fullWidth
                disabled={
                  !selectedEndCustomerArticleId || loadingButtonType !== null
                }
                loading={loadingButtonType === "Koppla"}
                onClick={async () => {
                  setLoadingButtonType("Koppla");
                  await handleSelectConnect();
                  setLoadingButtonType(null);
                }}
              >
                Koppla
              </LoadingButton>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Popper>
  );
};

export default ExtendedControls;
